import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import { useRef, useState, useEffect } from "react";
import { PartnerDecennale, PartnerInfos } from "types";
import { showNotification } from "context/notification.service";
import { useProjectStore } from "@store/projectStore";
import { partnersApi } from "@services/api/partners";
import { formatIsoDate } from "@services/timeHelpers";

interface Props {
  partnerId: string;
  partnerName: string;
  onClose: () => void;
}

export const ManageDecennalesModal = ({ partnerId, partnerName, onClose }: Props) => {
  const updatePartner = useProjectStore((state) => state.updatePartner);
  const storePartner = useProjectStore((state) => state.partners.find((p) => p.id === partnerId));
  const partnerListItem = useProjectStore((state) => state.partnersList.find((p) => p.id === partnerId));

  const [decennales, setDecennales] = useState<PartnerDecennale[]>(
    () => (storePartner?.decennales || partnerListItem?.decennales || []).slice()
  );

  // Keep in sync if store updates
  useEffect(() => {
    const source = storePartner || partnerListItem;
    if (source?.decennales) setDecennales(source.decennales.slice());
  }, [storePartner?.decennales, partnerListItem?.decennales]);

  // --- Add form state ---
  const [showAddForm, setShowAddForm] = useState(decennales.length === 0);
  const [newStartDate, setNewStartDate] = useState("");
  const [newEndDate, setNewEndDate] = useState("");
  const [newFile, setNewFile] = useState<File | null>(null);
  const [newFilePreview, setNewFilePreview] = useState<string | null>(null);
  const addFileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  // --- Edit state ---
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editStartDate, setEditStartDate] = useState("");
  const [editEndDate, setEditEndDate] = useState("");
  const [savingDates, setSavingDates] = useState(false);

  // --- Preview state ---
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  function refreshFromUpdated(updated: PartnerInfos) {
    updatePartner(updated);
    setDecennales(updated.decennales?.slice() || []);
  }

  // --- Add handlers ---
  function handleFileSelect(file: File) {
    if (file.type !== "application/pdf") {
      showNotification({ type: "error", message: "Seuls les fichiers PDF sont acceptés" });
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      showNotification({ type: "error", message: "Le fichier ne doit pas dépasser 20 Mo" });
      return;
    }
    setNewFile(file);
    setNewFilePreview(URL.createObjectURL(file));
  }

  async function handleAdd() {
    if (!newStartDate || !newEndDate) {
      showNotification({ type: "error", message: "Veuillez renseigner les dates de début et de fin" });
      return;
    }
    if (newStartDate >= newEndDate) {
      showNotification({ type: "error", message: "La date de fin doit être postérieure à la date de début" });
      return;
    }
    if (!newFile) {
      showNotification({ type: "error", message: "Veuillez sélectionner un fichier PDF" });
      return;
    }
    setUploading(true);
    try {
      const updated = await partnersApi.uploadDecennale(partnerId, newStartDate, newEndDate, newFile);
      refreshFromUpdated(updated);
      setShowAddForm(false);
      setNewStartDate("");
      setNewEndDate("");
      setNewFile(null);
      setNewFilePreview(null);
      showNotification({ type: "success", message: "Décennale ajoutée" });
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.message || "";
      showNotification({
        type: "error",
        message: detail ? `Erreur lors de l'upload : ${detail}` : "Erreur lors de l'upload",
      });
      console.error("Decennale upload error:", error?.response?.data || error);
    } finally {
      setUploading(false);
    }
  }

  // --- Edit handlers ---
  function startEditing(d: PartnerDecennale) {
    const key = d.start_date || (d.year ? `${d.year}-01-01` : "");
    setEditingKey(key);
    setEditStartDate(d.start_date || (d.year ? `${d.year}-01-01` : ""));
    setEditEndDate(d.end_date || (d.year ? `${d.year}-12-31` : ""));
  }

  async function handleSaveDates() {
    if (!editingKey || !editStartDate || !editEndDate) return;
    if (editStartDate >= editEndDate) {
      showNotification({ type: "error", message: "La date de fin doit être postérieure à la date de début" });
      return;
    }
    setSavingDates(true);
    try {
      const updated = await partnersApi.updateDecennaleDates(partnerId, editingKey, editStartDate, editEndDate);
      refreshFromUpdated(updated);
      setEditingKey(null);
      showNotification({ type: "success", message: "Dates mises à jour" });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la mise à jour" });
    } finally {
      setSavingDates(false);
    }
  }

  // --- Delete handler ---
  async function handleDelete(startDate: string) {
    try {
      const updated = await partnersApi.deleteDecennale(partnerId, startDate);
      refreshFromUpdated(updated);
      showNotification({ type: "success", message: "Décennale supprimée" });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la suppression" });
    }
  }

  const sorted = decennales.slice().sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));

  return (
    <>
      <BaseModal
        title={`Décennales — ${partnerName}`}
        startIcon={<Icon1 name="file" size={20} />}
        onClose={onClose}
        width="max-w-xl"
      >
        <div className="bg-white flex flex-col gap-3 p-4 max-h-[70vh] overflow-y-auto">
          {/* Existing décennales */}
          {sorted.map((d) => {
            const decKey = d.start_date || (d.year ? `${d.year}-01-01` : "");
            const isEditing = editingKey === decKey;
            const label = d.start_date && d.end_date
              ? `${formatIsoDate(d.start_date)} → ${formatIsoDate(d.end_date)}`
              : d.year ? `Année ${d.year}` : "Décennale";

            if (isEditing) {
              return (
                <div
                  key={d.start_date || d.year}
                  className="flex flex-col gap-2 px-3 py-3 bg-blue-50 rounded-lg border border-blue-200"
                >
                  <div className="flex flex-row gap-2">
                    <div className="flex flex-col gap-1 flex-1">
                      <label className="text-h5 text-neutral-500">Début</label>
                      <input
                        type="date"
                        value={editStartDate}
                        onChange={(e) => setEditStartDate(e.target.value)}
                        className="border border-neutral-300 rounded-lg px-2 py-1.5 text-h4"
                      />
                    </div>
                    <div className="flex flex-col gap-1 flex-1">
                      <label className="text-h5 text-neutral-500">Fin</label>
                      <input
                        type="date"
                        value={editEndDate}
                        onChange={(e) => setEditEndDate(e.target.value)}
                        className="border border-neutral-300 rounded-lg px-2 py-1.5 text-h4"
                      />
                    </div>
                  </div>
                  <div className="flex flex-row gap-2 justify-end">
                    <SecondaryButton variant="small" onClick={() => setEditingKey(null)}>
                      Annuler
                    </SecondaryButton>
                    <Button
                      variant="small"
                      label={savingDates ? "..." : "Enregistrer"}
                      onClick={handleSaveDates}
                    />
                  </div>
                </div>
              );
            }

            return (
              <div
                key={d.start_date || d.year}
                className="flex flex-row items-center gap-2 px-3 py-2.5 bg-neutral-50 rounded-lg border border-neutral-200"
              >
                <button
                  onClick={() => d.url && setPreviewUrl(d.url)}
                  className={`text-h3 font-medium flex-1 text-left flex items-center gap-2 ${d.url ? "text-neutral-700 hover:text-black cursor-pointer" : "text-neutral-400"}`}
                  title={d.url ? "Voir le PDF" : undefined}
                >
                  <Icon1 name="pdf" size={16} />
                  {label}
                </button>
                <button
                  onClick={() => startEditing(d)}
                  className="text-neutral-400 hover:text-neutral-600"
                  title="Modifier les dates"
                >
                  <Icon1 name="modify" size={18} />
                </button>
                {d.url && (
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-neutral-400 hover:text-neutral-600"
                    title="Ouvrir dans un nouvel onglet"
                  >
                    <Icon1 name="open" size={18} />
                  </a>
                )}
                <button
                  onClick={() => handleDelete(d.start_date)}
                  className="text-red-400 hover:text-red-600"
                  title="Supprimer"
                >
                  <Icon1 name="delete" size={18} />
                </button>
              </div>
            );
          })}

          {sorted.length === 0 && !showAddForm && (
            <div className="text-h3 text-neutral-400 text-center py-4">
              Aucune décennale enregistrée
            </div>
          )}

          {/* Add form */}
          {showAddForm ? (
            <div className="flex flex-col gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-200">
              <div className="text-h3 font-medium text-neutral-600">Nouvelle décennale</div>
              <div className="flex flex-row gap-2">
                <div className="flex flex-col gap-1 flex-1">
                  <label className="text-h5 text-neutral-500">Début</label>
                  <input
                    type="date"
                    value={newStartDate}
                    onChange={(e) => setNewStartDate(e.target.value)}
                    className="border border-neutral-300 rounded-lg px-2 py-1.5 text-h4"
                  />
                </div>
                <div className="flex flex-col gap-1 flex-1">
                  <label className="text-h5 text-neutral-500">Fin</label>
                  <input
                    type="date"
                    value={newEndDate}
                    onChange={(e) => setNewEndDate(e.target.value)}
                    className="border border-neutral-300 rounded-lg px-2 py-1.5 text-h4"
                  />
                </div>
              </div>

              {/* File drop / select */}
              <input
                ref={addFileRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelect(file);
                }}
              />
              {newFilePreview ? (
                <div className="flex flex-col gap-2">
                  <iframe
                    src={newFilePreview}
                    className="w-full rounded-lg border border-neutral-200"
                    style={{ height: 200 }}
                    title="Aperçu PDF"
                  />
                  <div className="flex flex-row items-center gap-2">
                    <span className="text-h4 text-neutral-500 flex-1 truncate">{newFile?.name}</span>
                    <button
                      onClick={() => {
                        setNewFile(null);
                        setNewFilePreview(null);
                        if (addFileRef.current) addFileRef.current.value = "";
                      }}
                      className="text-red-400 hover:text-red-600"
                    >
                      <Icon1 name="delete" size={16} />
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="border-2 border-dashed border-neutral-300 rounded-lg p-6 flex flex-col items-center gap-1 cursor-pointer hover:border-neutral-400 transition-colors"
                  onClick={() => addFileRef.current?.click()}
                >
                  <Icon1 name="join" size={20} color="grey" />
                  <span className="text-h4 text-neutral-400">Cliquez pour sélectionner un PDF</span>
                </div>
              )}

              <div className="flex flex-row gap-2 justify-end">
                <SecondaryButton variant="small" onClick={() => {
                  setShowAddForm(false);
                  setNewFile(null);
                  setNewFilePreview(null);
                  setNewStartDate("");
                  setNewEndDate("");
                }}>
                  Annuler
                </SecondaryButton>
                <Button
                  variant="small"
                  label={uploading ? "Upload..." : "Ajouter"}
                  onClick={handleAdd}
                />
              </div>
            </div>
          ) : (
            <SecondaryButton
              startIcon={<Icon1 name="plus" size={16} />}
              variant="small"
              onClick={() => setShowAddForm(true)}
            >
              Ajouter une décennale
            </SecondaryButton>
          )}
        </div>
        <div className="border-t-1 p-4 flex flex-row justify-end">
          <Button variant="small" label="Fermer" onClick={onClose} />
        </div>
      </BaseModal>

      {/* PDF Preview overlay */}
      {previewUrl && (
        <BaseModal
          title="Aperçu décennale"
          startIcon={<Icon1 name="pdf" size={20} />}
          onClose={() => setPreviewUrl(null)}
          display="fullscreen"
          width="max-w-6xl"
        >
          <div className="flex flex-col h-full">
            <div className="flex-1 overflow-hidden" style={{ height: "calc(100vh - 120px)" }}>
              <iframe src={previewUrl} className="w-full h-full" title="Décennale PDF" />
            </div>
            <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
              <Button
                variant="small"
                label="Télécharger"
                onClick={() => window.open(previewUrl, "_blank")}
              />
            </div>
          </div>
        </BaseModal>
      )}
    </>
  );
};
